/**
 * B-158 (Alvin pin 572, "What is Overture Maps?"): the link at the bottom of an activity's detail names where the click
 * goes in plain words, never the dataset a place came from. Map datasets (Overture Maps, OpenStreetMap) store the place's
 * own website as `url` when it has one, else an OpenStreetMap map link (DATA-MAP: Overture rows).
 *   own site      -> "Visit laphil.com"            (scout rows: source_name is the venue host; map rows with a website)
 *   listing site  -> "View on Ticketmaster"        (a named third-party listing source, link on its host)
 *   map link      -> "View on map" + "Found on a public map of places"
 */
export type SourceItem = { url: string | null; source_name: string | null }
export type SourceLine =
  | { kind: 'own'; site: string; mapFound: boolean }
  | { kind: 'listing'; source: string }
  | { kind: 'map' }
  | { kind: 'none'; mapFound: boolean }

const MAP_DATASETS = new Set(['overture maps', 'openstreetmap', 'osm'])
const MAP_HOSTS = /(^|\.)(openstreetmap\.org|osm\.org|google\.[a-z.]+|goo\.gl|maps\.apple\.com)$/i

export function hostOf(u: string): string | null {
  try {
    const h = new URL(u).hostname.replace(/^www\./i, '').toLowerCase()
    return h || null
  } catch {
    return null
  }
}

const bare = (s: string) => s.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '')

export function sourceLine(item: SourceItem): SourceLine {
  const name = (item.source_name ?? '').trim()
  const fromMap = MAP_DATASETS.has(name.toLowerCase())
  const h = item.url ? hostOf(item.url) : null
  if (!h) return { kind: 'none', mapFound: fromMap }
  if (MAP_HOSTS.test(h) && (fromMap || !name || /maps?/i.test(item.url ?? ''))) return { kind: 'map' }
  // a named listing site (not a dataset, not a bare domain that is the link's own host) whose link is on its host
  const nameIsHost = name.includes('.') && !name.includes(' ')
  if (name && !fromMap && !nameIsHost) {
    const words = name.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3)
    const onItsHost = words.some((w) => h.includes(w))
    if (onItsHost) return { kind: 'listing', source: name }
  }
  if (nameIsHost && bare(name) !== h && !h.endsWith(`.${bare(name)}`) && !bare(name).endsWith(`.${h}`)) {
    // the link went to a ticketing or other host: name the host the click opens
    return { kind: 'own', site: h, mapFound: false }
  }
  return { kind: 'own', site: h, mapFound: fromMap }
}
